/**
 * 价格趋势图表组件
 * 验证需求：6.1, 6.2, 6.3, 6.4, 6.5
 * 使用ECharts绘制折线图，支持双Y轴、时间范围选择、时间粒度切换
 */
import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { PriceData } from '../types';
import { formatDateForDisplay } from '../utils/dateUtils';
import type { TimeGranularity } from '../utils/dateUtils';
import { formatPrice, calculatePriceChange } from '../utils/priceUtils';

interface PriceTrendChartProps {
  data: PriceData[];
  title?: string;
  showDualAxis?: boolean; // 是否显示双Y轴
  granularity?: TimeGranularity;
  height?: number;
  loading?: boolean;
  theme?: 'light' | 'dark';
}

export default function PriceTrendChart({
  data,
  title = '价格趋势',
  showDualAxis = true,
  granularity = 'day',
  height = 400,
  loading = false,
  theme = 'light',
}: PriceTrendChartProps) {
  const option: EChartsOption = useMemo(() => {
    const isDark = theme === 'dark';
    const textColor = isDark ? '#fff' : '#434343'; // Darker gray for better contrast
    const axisColor = isDark ? 'rgba(255, 255, 255, 0.5)' : '#d9d9d9'; // AntD border color
    const splitLineColor = isDark ? 'rgba(255, 255, 255, 0.1)' : '#f0f0f0';

    // Premium Color Palette
    const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d'];

    if (!data || data.length === 0) {
      return {
        title: { text: title, left: 'center', textStyle: { color: textColor, fontWeight: 500 } },
        graphic: { type: 'text', left: 'center', top: 'middle', style: { text: '暂无数据', fill: '#999' } },
      };
    }

    const dates = data.map((item) => formatDateForDisplay(item.date, granularity));
    const pricesCny = data.map((item) => item.price_cny);
    const pricesUsd = data.map((item) => item.price_usd);

    const config: EChartsOption = {
      backgroundColor: 'transparent',
      color: colors,
      title: {
        text: title,
        left: 'left',
        textStyle: { fontSize: 16, fontWeight: 600, color: '#1f1f1f' },
        padding: [0, 0, 10, 0], // Add padding below title
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)', // Glass effect base
        borderColor: '#e8e8e8',
        borderWidth: 1,
        padding: [10, 15],
        textStyle: { color: '#333', fontSize: 13 },
        extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.15); backdrop-filter: blur(4px); border-radius: 8px;', // Glassmorphism
        axisPointer: { type: 'line', lineStyle: { type: 'dashed', color: '#999' } },
        formatter: (params: any) => {
          // ... existing formatter logic maintained for content ...
          if (!Array.isArray(params) || params.length === 0) return '';
          const date = params[0].axisValue;
          const index = params[0].dataIndex;
          let html = `<div style="font-family: Inter, sans-serif;">`;
          html += `<div style="margin-bottom: 8px; font-weight: 600; color: #000;">${date}</div>`;
          params.forEach((param: any) => {
            html += `<div style="display: flex; justify-content: space-between; align-items: center; margin: 4px 0;">`;
            html += `<div><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${param.color};margin-right:8px;"></span>${param.seriesName}</div>`;
            html += `<div style="font-weight: 500; margin-left: 12px;">${param.seriesName.includes('人民币') ? formatPrice(param.value, 'CNY') : formatPrice(param.value, 'USD')}</div>`;
            html += `</div>`;
          });
          return html + '</div>';
        }
      },
      legend: {
        data: showDualAxis ? ['人民币价格', '美元价格'] : ['价格'],
        right: 0,
        top: 0,
        icon: 'circle',
        itemGap: 20,
        textStyle: { color: '#666' },
      },
      grid: {
        left: '10', right: '10', bottom: '10', top: '50',
        containLabel: true,
        show: false, // Clean look
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLine: { lineStyle: { color: axisColor } },
        axisLabel: { color: '#666', rotate: granularity === 'day' ? 45 : 0 },
        axisTick: { show: false } // Cleaner axis
      },
      yAxis: showDualAxis
        ? [
          {
            type: 'value',
            name: '民币 (元/吨)',
            position: 'left',
            alignTicks: true,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { color: '#666', formatter: (val: number) => `¥${val / 10000}万` }, // Simplified formatter
            splitLine: { lineStyle: { type: 'dashed', color: splitLineColor } }, // Dashed grid
          },
          {
            type: 'value',
            name: '美元 ($/磅)',
            position: 'right',
            alignTicks: true,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { color: '#666', formatter: (val: number) => `$${val.toFixed(1)}` },
            splitLine: { show: false },
          },
        ]
        : [{ type: 'value', splitLine: { lineStyle: { type: 'dashed' } } }],
      series: showDualAxis ? [
        {
          name: '人民币价格', type: 'line', data: pricesCny, smooth: true, yAxisIndex: 0,
          symbol: data.length === 1 ? 'circle' : 'none', // Show symbol if only 1 point
          symbolSize: 6,
          lineStyle: { width: 3, shadowColor: 'rgba(24, 144, 255, 0.3)', shadowBlur: 10, shadowOffsetY: 5 }, // Glow effect
          areaStyle: {
            color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(24, 144, 255, 0.2)' }, { offset: 1, color: 'rgba(24, 144, 255, 0)' }] }
          },
        },
        {
          name: '美元价格', type: 'line', data: pricesUsd, smooth: true, yAxisIndex: 1,
          symbol: data.length === 1 ? 'circle' : 'none',
          symbolSize: 6,
          lineStyle: { width: 3, shadowColor: 'rgba(82, 196, 26, 0.3)', shadowBlur: 10, shadowOffsetY: 5 },
          areaStyle: {
            color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(82, 196, 26, 0.2)' }, { offset: 1, color: 'rgba(82, 196, 26, 0)' }] }
          },
        }
      ] : [],
      dataZoom: [{ type: 'inside', start: 0, end: 100 }, { start: 0, end: 100, show: true, height: 20, bottom: 0, borderColor: 'transparent', backgroundColor: '#f5f5f5', fillerColor: 'rgba(24, 144, 255, 0.1)', handleStyle: { color: '#1890ff' } }],
    };

    return config;
  }, [data, title, showDualAxis, granularity, theme]);

  return (
    <ReactECharts
      option={option}
      style={{ height: `${height}px`, width: '100%' }}
      showLoading={loading}
      theme={theme === 'dark' ? 'dark' : undefined}
      loadingOption={{ text: 'Loading...', color: '#1890ff', maskColor: 'rgba(255, 255, 255, 0.8)' }}
    />
  );
}
